import { Combobox, ComboboxInput, ComboboxOption, ComboboxOptions, Label } from '@headlessui/react';
import clsx from 'clsx';
import { useField, useFormikContext } from 'formik';
import { type ChangeEvent, Fragment, useState } from 'react';
import { MdClose } from 'react-icons/md/index';
import { getOptionClasses } from '../Lookup/utils';
import Select from '../Select';
import SimpleInput from '../SimpleInput';
import InputError from '~/components/feedback/InputError';
import type { IAusPostcodeInput } from '~/types/inputs';
import { getApiURL } from '~/utils/api';
import { getInputSizeClasses } from '~/utils/form';
import './style.css';

interface ISuburbOption {
	suburb: string;
	state: string;
	postcode: string;
}

export default function AusPostCode({
	name,
	label,
	width,
	type,
	required,
	promoSlug,
	...rest
}: IAusPostcodeInput) {
	const [value, setValue] = useState('');
	const [isFetching, setIsFetching] = useState(false);
	const [manualMode, setManualMode] = useState(false);
	const [failedToFetch, setFailedToFetch] = useState(false);
	const [field, meta] = useField(name);
	const [options, setOptions] = useState<ISuburbOption[]>([]);
	const { submitCount, setFieldValue } = useFormikContext();
	const isError = (meta.touched || submitCount > 0) && !!meta.error;

	const resetField = (shouldToggleManualMode = false) => {
		setFieldValue('aus_postcode:manualMode', shouldToggleManualMode);
		setFieldValue(name, '');
		setFieldValue('suburb', '');
		setFieldValue('postcode', '');
		setFieldValue('state', '');
	};

	const handleSelection = (option: ISuburbOption | null) => {
		setValue('');
		setOptions([]);

		// If the value is null, that means they picked the "manual entry" option
		if (option === null) {
			setManualMode(true);
			resetField(true);
			return;
		}

		const stringifiedValue = `${option.suburb}, ${option.postcode}, ${option.state}`;

		setFieldValue(name, stringifiedValue);
	};

	const onInputChange = async (event: ChangeEvent<HTMLInputElement>) => {
		const newValue = event.target.value;

		setValue(newValue);
		setFailedToFetch(false);

		// If we are changing the internal value (i.e. search value) and there's currently no search value,
		// Clear what is in Formik state as this means we are initiating a new search flow
		if (!value) {
			resetField(false);
		}

		if (newValue.length < 2) {
			setOptions([]);
			return;
		}

		setIsFetching(true);

		const response = await fetch(`${getApiURL(promoSlug)}/aus-postcodes?search=${newValue}`, {
			method: 'GET',
		});

		if (response.ok) {
			const data = await response.json();

			setOptions(data?.data || []);
		} else {
			setFailedToFetch(true);
		}

		setIsFetching(false);
	};

	if (manualMode) {
		return (
			<>
				<SimpleInput
					label="Postcode"
					name="postcode"
					required
					width="full"
					type="text"
					description=""
				/>
				<SimpleInput
					label="Suburb"
					name="suburb"
					required
					width="full"
					type="text"
					description=""
				/>
				<Select
					label="State"
					name="state"
					required
					width="full"
					type="select"
					description=""
					options={[
						{ label: 'Select State', value: '' },
						{ label: 'NSW', value: 'NSW' },
						{ label: 'VIC', value: 'VIC' },
						{ label: 'QLD', value: 'QLD' },
						{ label: 'SA', value: 'SA' },
						{ label: 'WA', value: 'WA' },
						{ label: 'TAS', value: 'TAS' },
						{ label: 'NT', value: 'NT' },
						{ label: 'ACT', value: 'ACT' },
					]}
				/>
			</>
		);
	}

	return (
		<Combobox
			className={clsx(getInputSizeClasses(width), 'w-full')}
			as="div"
			value={field.value}
			onChange={handleSelection}
		>
			<div
				className={clsx(
					'mx-auto simple-input-container rounded-tl-input rounded-tr-input relative h-input w-full',
				)}
			>
				{field.value && (
					<span className="lookup-value absolute w-full flex items-center justify-between bottom-2 left-0 px-5">
						<span className="pointer-events-none whitespace-nowrap overflow-hidden overflow-ellipsis font-body-large w-full outline-none text-left">
							{field.value}
						</span>
						<button
							onClick={() => {
								resetField();
							}}
							className="lookup-clear-button absolute flex items-center justify-center right-0 bottom-0 w-[44px] h-[44px]"
						>
							<MdClose size={20} />
						</button>
					</span>
				)}
				<ComboboxInput
					{...rest}
					value={value}
					onChange={onInputChange}
					type={type}
					className="simple-input-input lookup-input peer placeholder-transparent text-base w-full outline-none pl-5 pr-5 pt-6 pb-1 h-full"
					id={name}
					aria-invalid={isError}
					aria-describedby={`${name}_error`}
					placeholder=" "
				/>
				<Label
					htmlFor={name}
					className={clsx(
						'simple-input-label pointer-events-none transition-all peer-focus:top-3 top-3 peer-focus:text-xs text-xs absolute left-5',
						field.value
							? 'top-3 text-xs'
							: 'peer-placeholder-shown:top-6 peer-placeholder-shown:font-large-body font-large-body',
					)}
				>
					{label}
					{required && !label.endsWith('*') ? <sup>*</sup> : null}
				</Label>
				<div
					className={clsx(
						'simple-input-border absolute -bottom-[2px] left-0 w-full h-[2px]',
						isError ? 'is-error' : '',
					)}
				/>
				<ComboboxOptions
					anchor="bottom start"
					className="relative w-[--input-width] translate-y-[2px] z-20 list-none"
				>
					{options.map(locale => (
						<ComboboxOption
							as={Fragment}
							key={`${locale.suburb}-${locale.postcode}`}
							value={locale}
						>
							{({ active }) => (
								<li className={getOptionClasses(active)}>
									{locale.suburb} ({locale.postcode})
								</li>
							)}
						</ComboboxOption>
					))}
					{value.length >= 3 && options.length === 0 && !isFetching && (
						<ComboboxOption as={Fragment} value={null}>
							{({ active }) => (
								<li className={getOptionClasses(active)}>
									{failedToFetch ? (
										<>Search failed. Click here to enter manually.</>
									) : (
										<>Can&rsquo;t find your suburb? Enter it manually.</>
									)}
								</li>
							)}
						</ComboboxOption>
					)}
				</ComboboxOptions>
				<InputError name={name} isError={isError}>
					{meta.error}
				</InputError>
			</div>
		</Combobox>
	);
}
